<template>
  <div
    class="d-flex flex-column"
    :class="{'flex': listVisible}"
  >

    <div v-if="listVisible" class="flex d-flex flex-column">
      <v-alert v-if="error" type="error" class="mb-4" text>
        {{errorMessage}}
      </v-alert>

      <div v-if="loading" class="text-center py-4 px-3">
        <v-progress-circular
          :width="3"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>

      <template v-if="!loading && !error">
        <v-list dense>
          <v-list-item v-for="(file, index) in files" :key="file.id">
            <v-list-item-icon class="mr-3">
              <v-tooltip v-if="file.owner.isCurrentUser && file.participants && file.participants.length" top>
                <template #activator="{on}">
                  <div v-on="on">
                    <v-badge color="accent" icon="far fa-lock" overlap bottom>
                      <i :class="_icon(file.name)" class="file-type"></i>
                    </v-badge>
                  </div>
                </template>
                <strong>Geteilt mit:</strong><br>
                <span v-for="participant in _sort(file.participants)" :key="participant.id">
                  {{participant.name}}<br>
                </span>
              </v-tooltip>
              <i v-else :class="_icon(file.name)" class="file-type"></i>
            </v-list-item-icon>
            <v-list-item-content class="align-self-start">
              <v-list-item-title :title="file.name">{{file.name}}</v-list-item-title>
              <v-list-item-subtitle v-if="file.description" :title="file.description">
                {{file.description}}
              </v-list-item-subtitle>
              <v-list-item-subtitle>
                <small class="text-truncate">
                  von
                  <i v-if="file.owner.isCurrentUser">dir</i>
                  <i v-else :title="file.owner.name">{{_userName(file.owner.name)}}</i>
                  • {{file.created | moment('L[,] LT')}}
                </small>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="mt-2 mb-2 align-self-start">
              <v-list-item-subtitle class="mb-1">
                <small>{{_size(file.size)}}</small>
              </v-list-item-subtitle>
              <div>
                <v-btn
                  v-if="file.owner.isCurrentUser || room.owner.isCurrentUser"
                  color="primary"
                  icon
                  x-small
                  title="Freigeben"
                  class="mr-3"
                  @click="() => _share(index)"
                >
                  <v-icon>far fa-share-alt</v-icon>
                </v-btn>

                <v-btn
                  :href="'/api/files/' + file.id + file.ext"
                  :download="file.name"
                  :target="downloadTarget"
                  color="primary"
                  title="Herunterladen"
                  icon
                  x-small
                >
                  <v-icon>far fa-arrow-to-bottom</v-icon>
                </v-btn>

                <v-btn
                  v-if="file.owner.isCurrentUser || room.owner.isCurrentUser"
                  :loading="file.loading"
                  color="error"
                  title="Löschen"
                  class="ml-3"
                  icon
                  x-small
                  @click="() => _delete(index)"
                >
                  <v-icon>far fa-trash-alt</v-icon>
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-pagination
          v-if="files.length && pages_count > 1"
          class="mt-auto mb-1"
          :value="page"
          :length="pages_count"
          :total-visible="5"
          prev-icon="far fa-chevron-left"
          next-icon="far fa-chevron-right"
          @input="$event !== page && $router.replace({path: $route.path, query: {...$route.query, page: $event}})"
        ></v-pagination>
      </template>
    </div>
    <div v-if="room.quota > 0" class="mt-auto px-3 mb-1">
      <small class="d-flex justify-space-between">
        <span>{{_size(total_size)}} verbraucht</span>
        <span>
          {{_size(quota_free > 0 ? quota_free : 0)}} von
          {{_size(room.quota)}} frei
        </span>
      </small>
      <v-progress-linear :value="quota_used_percent"></v-progress-linear>
    </div>
  </div>
</template>

<script>
  import getClassNameForExtension from 'font-awesome-filetypes';
  import {formatFileSize, compareAlphabetically, downloadSupported} from '../../assets/js/utils';
  import {prompt} from '../Prompt';
  import {getUserName} from '../peers/peer-utils';
  import eventBus from '../../assets/js/event-bus';


  export default {
    name: 'FilesList',

    props: {
      page: {
        type: Number,
        default: 1
      },
      limit: {
        type: Number,
        default: 10
      }
    },

    data() {
      return {
        files: [],
        loading: false,
        error: false,
        errorMessage: '',
        metadata: {},
        downloadTarget: downloadSupported ? '_self' : '_blank'
      };
    },

    computed: {
      room() {
        return this.$store.getters['share/getRoom'] || null;
      },

      pages_count() {
        const {metadata, limit} = this;
        if (!metadata.total_count) {
          return 0;
        }
        return Math.ceil(metadata.total_count / limit);
      },

      total_size() {
        return this.metadata.total_size ?? 0;
      },

      quota() {
        return this.room?.quota ?? 0;
      },

      quota_free() {
        return this.quota - this.total_size;
      },

      quota_used_percent() {
        return this.total_size / this.quota * 100;
      },

      listVisible() {
        return !!(this.files?.length || this.loading || this.error);
      }
    },

    watch: {
      listVisible(listVisible) {
        this.$emit('visible', listVisible);
      },

      quota_free(quota_free) {
        this.$emit('quota-free', quota_free);
      }
    },

    mounted() {
      this.$options.watch.listVisible.call(this, this.listVisible);
    },

    methods: {
      async loadFiles(opt_noLoadingAnimation = false) {
        if (!this.room || this.room.id === 'local' || this.loading) {
          return;
        }

        this.error = false;
        if (!opt_noLoadingAnimation) {
          this.loading = true;
          this.files = [];
        }

        try {
          let {page} = this;
          if (page < 1) {
            page = 1;
          }
          let {pages_count} = this;
          if (pages_count && page > pages_count) {
            page = pages_count;
          }

          const {$router, $route} = this;
          if (page !== this.page) {
            this.loading = false;
            return $router.replace({path: $route.path, query: {...$route.query, page}});
          }

          const {limit} = this;
          const {data} = await this.$api.post('FilesList', {
            roomId: this.room.id,
            offset: (page - 1) * limit,
            limit
          });
          this.loading = false;
          this.files = data.results || [];
          this.metadata = data.metadata || {};

          // Check pages_count again (on first load).
          pages_count = this.pages_count;
          if (page > pages_count) {
            return $router.replace({path: $route.path, query: {...$route.query, page: pages_count}});
          }

        } catch (error) {
          this.loading = false;
          this.errorMessage = 'Dateien konnten nicht geladen werden.';
          this.error = true;
        }
      },

      _icon(name) {
        const extension = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
        const className = 'fas ' + getClassNameForExtension(extension);

        return className;
      },

      _size(size) {
        return formatFileSize(size);
      },

      _userName(name) {
        const peer = {name: {userName: name}};
        return getUserName(peer);
      },

      async _share(index) {
        const file = this.files[index];
        eventBus.$emit('filePublicShare', file);
      },

      async _delete(index) {
        const file = this.files[index];

        this.error = false;

        // Show loading animation.
        this.$set(this.files, index, {
          ...this.files[index],
          loading: true
        });

        try {
          await prompt({
            title: `Datei "${file.name}" löschen?`,
            content: 'Datei wird für alle Benutzer unwiderruflich gelöscht.'
          });

          await this.$api.post('FileDelete', {id: file.id});

        } catch (error) {
          if (error && error.message !== 'declined') {
            this.errorMessage = `Datei "${file.name}" konnte nicht gelöscht werden.`;
            this.error = true;
          }
        }

        // Remove loading animation.
        this.$set(this.files, index, {
          ...this.files[index],
          loading: false
        });
      },

      _sort(participants) {
        participants = [...participants];

        participants.sort((a, b) => compareAlphabetically(a.name, b.name));

        return participants;
      }
    }
  };
</script>

<style lang="scss" scoped>
  nav::v-deep {
    $font-size: 12px;
    $size: 25px;

    .v-pagination__item {
      font-size: $font-size;
      min-width: $size;
      height: $size;
    }

    .v-pagination__navigation {
      width: $size;
      height: $size;
      margin: 0.3rem;

      .v-icon {
        font-size: $font-size;
      }
    }
  }

  .v-progress-linear {
    border-radius: 3px;
  }
</style>
